import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Users, UserCheck, UserX, AlertCircle, ShieldAlert } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';


function Employees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeToToggle, setEmployeeToToggle] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);

  useEffect(() => {
    checkAccessAndLoad();
  }, []);

  const checkAccessAndLoad = async () => {
    try {
      const adminRes = await api.get(`/employees/check-admin`);
      setIsAdmin(adminRes.data.is_admin);
      
      if (!adminRes.data.is_admin) {
        setLoading(false);
        return;
      }
      
      await loadEmployees();
    } catch (error) {
      console.error('Failed to check access:', error);
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await api.get(`/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to load employees:', error);
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        toast.error('Failed to load employees');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (employee) => {
    try {
      const newStatus = !employee.is_active;
      await api.patch(
        `/employees/${employee.user_id}`,
        { is_active: newStatus },
        
      );
      toast.success(newStatus ? 'Employee activated' : 'Employee deactivated');
      loadEmployees();
      setEmployeeToToggle(null);
    } catch (error) {
      console.error('Failed to update employee:', error);
      toast.error('Failed to update employee');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show access denied if not admin
  if (isAdmin === false) {
    return (
      <div className="max-w-[600px] mx-auto p-6">
        <div className="bg-white border border-rose-200 rounded-xl p-8 text-center shadow-soft">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-8 h-8 text-rose-600" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mb-2">Access Denied</h1>
          <p className="text-stone-600 mb-6">
            Only administrators can view and manage employees. Please contact your system administrator if you need access.
          </p>
          <Button onClick={() => navigate('/')} className="bg-primary hover:bg-primary-hover text-stone-900 rounded-xl shadow-gold font-semibold">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const activeEmployees = employees.filter((e) => e.is_active);
  const inactiveEmployees = employees.filter((e) => !e.is_active);

  return (
    <div className="max-w-[1600px] mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-stone-900 mb-2">
          Employee Management
        </h1>
        <p className="text-sm text-stone-600 leading-relaxed">
          Manage employees who can be assigned to tickets. Employees are automatically added when they first login with @vaaree.com email.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">
              Total Employees
            </p>
            <Users className="w-5 h-5 text-stone-400" strokeWidth={1.5} />
          </div>
          <p className="text-3xl font-bold text-stone-900">{employees.length}</p>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">
              Active
            </p>
            <UserCheck className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
          </div>
          <p className="text-3xl font-bold text-stone-900">{activeEmployees.length}</p>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">
              Inactive
            </p>
            <UserX className="w-5 h-5 text-stone-400" strokeWidth={1.5} />
          </div>
          <p className="text-3xl font-bold text-stone-900">{inactiveEmployees.length}</p>
        </div>
      </div>

      {/* Active Employees */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold tracking-tight text-stone-900 mb-4">
          Active Employees
        </h2>
        <div className="bg-white border border-stone-200 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Added On
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {activeEmployees.map((employee) => (
                  <tr key={employee.user_id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-medium text-stone-800">{employee.name}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-600">{employee.email}</td>
                    <td className="px-6 py-4 text-sm text-stone-600">
                      {new Date(employee.added_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        onClick={() => setEmployeeToToggle(employee)}
                        variant="outline"
                        size="sm"
                        data-testid={`deactivate-employee-${employee.user_id}`}
                        className="bg-white border border-stone-200 hover:bg-stone-50 text-stone-700"
                      >
                        Deactivate
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {activeEmployees.length === 0 && (
              <div className="text-center py-12 text-stone-500">
                <p>No active employees</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inactive Employees */}
      {inactiveEmployees.length > 0 && (
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-stone-900 mb-4">
            Inactive Employees
          </h2>
          <div className="bg-white border border-stone-200 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Added On
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {inactiveEmployees.map((employee) => (
                    <tr key={employee.user_id} className="hover:bg-stone-50 transition-colors opacity-60">
                      <td className="px-6 py-4">
                        <p className="font-medium text-stone-800">{employee.name}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-600">{employee.email}</td>
                      <td className="px-6 py-4 text-sm text-stone-600">
                        {new Date(employee.added_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          onClick={() => setEmployeeToToggle(employee)}
                          variant="outline"
                          size="sm"
                          data-testid={`activate-employee-${employee.user_id}`}
                          className="bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 text-emerald-700"
                        >
                          Activate
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={!!employeeToToggle} onOpenChange={() => setEmployeeToToggle(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {employeeToToggle?.is_active ? 'Deactivate' : 'Activate'} Employee
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {employeeToToggle?.is_active ? 'deactivate' : 'activate'}{' '}
              <strong>{employeeToToggle?.name}</strong>?
              {employeeToToggle?.is_active && (
                <span className="block mt-2 text-amber-600">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Deactivated employees cannot be assigned to tickets.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleToggleActive(employeeToToggle)}
              className="bg-primary hover:bg-primary-hover"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Employees;
